'use client'

import { useAuth } from '@lastbrand/fe-common/universal-auth'
import { LinkIcon, ShareIcon } from '@lastbrand/web-ui/icons'
import { TextLink } from '@lastbrand/web-ui/text-link'
import { css } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'
import { useEffect, useState } from 'react'

import { Link } from '@src/quince-components/src'
import useFetch from '@src/quince-site-common/src/hooks/useFetch'
import { useAppInstallAccountsPresenter } from '@src/quince-site-common/src/organisms/appLayout/molecules/footer/molecules/app-install/app-install.presenter'
import wishlistService from '@src/quince-site-common/src/services/wishlistService'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import {
  shareLinkCompactCss,
  shareLinkUppercaseCss,
  shareMsgCss,
  shareMsgErrorMobileCss,
  shareMsgShownCss,
  shareMsgWrapperCss,
  shareMsgWrapperErrorCss,
  shareWrapperCompactCss,
  shareWrapperCss,
} from './wishlist-share-section-ds.styles'

import type { WishlistShareSectionDSProps } from './wishlist-share-section-ds.types'

export function WishlistShareSectionDS({ wishListProductList }: WishlistShareSectionDSProps) {
  const [showMsg, setShowMsg] = useState<boolean>(false)
  const [hasError, setHasError] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const { shouldRender: shouldRenderAppInstallAccounts } = useAppInstallAccountsPresenter({ placement: 'wishlist' })
  const shareLinkCss = shouldRenderAppInstallAccounts ? shareLinkCompactCss : shareLinkUppercaseCss

  const {
    fetchData: getUserToken,
    isLoading: isTokenLoading,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    response: wishlistTokenResponse,
  } = useFetch(wishlistService.getWishlistToken, {})

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
  const { isLoggedIn } = useAuth()

  const showMessage = () => {
    setShowMsg(true)
    setTimeout(() => {
      setShowMsg(false)
    }, 3000)
  }

  // @i18n-ignore
  const TOKEN_NOT_GENERATED_ERROR_MESSAGE = 'Tokken not generated from API!!!'

  const handleWishlistShare = async () => {
    if (isLoading) return
    try {
      setIsLoading(true)
      setHasError(false)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (!wishlistTokenResponse?.wishlist_token) {
        throw new Error(TOKEN_NOT_GENERATED_ERROR_MESSAGE)
      }
      trackEvent(TELEMETRY_EVENT_NAMES.SHARE_WISHLIST, {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        wishlistToken: wishlistTokenResponse?.wishlist_token,
        wishlistProductList: wishListProductList,
      })
      const url = new URL(window.location.href)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      url.searchParams.append('id', wishlistTokenResponse.wishlist_token)
      url.searchParams.append('utm_medium', 'organic_social')
      url.searchParams.append('utm_source', 'wishlist_share')
      await navigator.clipboard.writeText(url.href)
    } catch (err) {
      setHasError(true)
      console.error('Failed to copy: ', err)
    } finally {
      showMessage()
      setIsLoading(false)
    }
  }

  function onShareButtonClick() {
    handleWishlistShare().catch((err: unknown) => {
      console.error('Error while sharing wishlist, onShareButtonClick():: ', err)
    })
  }

  useEffect(() => {
    if (isLoggedIn) {
      getUserToken().catch((err: unknown) => {
        console.error('Error while fetching wishlist token, getUserToken():: ', err)
      })
    }
  }, [getUserToken, isLoggedIn])

  const { t } = useTranslation('wishlist')

  return (
    <div className={css(shareWrapperCss, shouldRenderAppInstallAccounts ? shareWrapperCompactCss : {})}>
      {isLoggedIn ? (
        <TextLink
          css={shareLinkCss}
          disabled={isLoading || isTokenLoading}
          onClick={onShareButtonClick}
          trailingIcon={shouldRenderAppInstallAccounts ? <ShareIcon aria-hidden /> : <LinkIcon aria-hidden />}
        >
          {t('share')}
        </TextLink>
      ) : (
        <TextLink
          css={shareLinkCss}
          LinkComponent={Link}
          href={`/log-in?redirecturi=${isBrowser() ? window.location.href : ''}`}
          trailingIcon={shouldRenderAppInstallAccounts ? <ShareIcon aria-hidden /> : <LinkIcon aria-hidden />}
        >
          {t('sign_in_to_share')}
        </TextLink>
      )}
      <div className={css(shareMsgWrapperCss, hasError ? shareMsgWrapperErrorCss : {})}>
        <div
          className={css(shareMsgCss, hasError ? shareMsgErrorMobileCss : {}, showMsg ? shareMsgShownCss : {})}
          role="status"
          data-testid="wishlist-share-message"
          data-show={showMsg}
        >
          {hasError ? t('something_went_wrong') : t('link_copied')}
        </div>
      </div>
    </div>
  )
}
